import Paper from '../../../shared/components/Paper/Paper';
import React, { useEffect, useMemo, useState } from 'react';
import Alert from '../../../shared/components/Alert/Alert';
import { useNavigate } from 'react-router-dom';
import Box from '../../../shared/components/Box/Box';
import Input from '../../../shared/components/Input/Input';
import Typography from '../../../shared/components/Typography/Typography';
import useLocalStorageState from '../../../hooks/useLocalStorageState';
import { createPartner, getCompanyDomains } from './partner.api';
import {
  EMPTY_PARTNER_FORM,
  PARTNER_RELATIONSHIP_STATUSES,
  PARTNER_ROLE_OPTIONS,
  ASSIGNABLE_PARTNER_ROLE_OPTIONS,
  PARTNER_SOURCES,
  buildPartnerPayload,
  restorePartnerCreationDraft,
  getPartnerLifecycle,
  normalizePartnerAgency,
  normalizePartnerContact,
  updatePartnerContactField,
} from './partnerModel';
import {
  validatePartner,
  validatePartnerAgency,
  validatePartnerContact,
} from './partnerValidation';
import './partnerCreationUx.scss';

const draftStorageKey = 'sas-erp-partner-creation-draft';

const initialCompany = {
  ...EMPTY_PARTNER_FORM,
  roles: [],
};

const emptyContact = {
  firstName: '',
  lastName: '',
  contactName: '',
  agencyClientKey: '',
  role: '',
  email: '',
  phone: '',
  mobilePhone: '',
  notes: '',
  _contactNameEdited: false,
};

const emptyAgency = {
  clientKey: '',
  name: '',
  socialReason: '',
  addressLine1: '',
  addressLine2: '',
  address: '',
  city: '',
  regionOrState: '',
  postalCode: '',
  countryCode: 'FR',
  phone: '',
  email: '',
  website: '',
  notes: '',
};

const hasValue = (item) => Object.entries(item).some(([key, value]) => (
  !['countryCode', 'agencyClientKey', 'clientKey', '_contactNameEdited'].includes(key) && String(value || '').trim()
));

const createAgencyDraft = () => ({
  ...emptyAgency,
  clientKey: `agency-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
});

function PartnerCreation() {
  const navigate = useNavigate();
  const [draft, setDraft] = useLocalStorageState(draftStorageKey, {
    activeStep: 0,
    company: initialCompany,
    contacts: [{ ...emptyContact }],
    agencies: [],
  });
  const [activeStep, setActiveStep] = useState(draft.activeStep || 0);
  const [company, setCompany] = useState(() => restorePartnerCreationDraft(draft.company));
  const [contacts, setContacts] = useState(draft.contacts?.length ? draft.contacts : [{ ...emptyContact }]);
  const [agencies, setAgencies] = useState(draft.agencies || []);
  const [domainOptions, setDomainOptions] = useState([]);
  const [subdomainOptions, setSubdomainOptions] = useState([]);
  const [domainsLoading, setDomainsLoading] = useState(false);
  const [subdomainsLoading, setSubdomainsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const steps = useMemo(() => [
    { key: 'company', label: 'Identité' },
    { key: 'address', label: 'Coordonnées' },
    { key: 'agencies', label: 'Compléments' },
    { key: 'contacts', label: 'Contacts' },
    { key: 'summary', label: 'Résumé' },
  ], []);

  const currentStep = steps[activeStep]?.key || 'company';

  useEffect(() => {
    setDraft({ activeStep, company, contacts, agencies });
  }, [activeStep, agencies, company, contacts, setDraft]);

  useEffect(() => {
    let isMounted = true;
    setDomainsLoading(true);

    getCompanyDomains()
      .then((domains) => {
        if (isMounted) {
          setDomainOptions(Array.isArray(domains) ? domains : []);
        }
      })
      .catch((loadError) => {
        if (isMounted) {
          setError(loadError.message || 'Impossible de charger les domaines.');
        }
      })
      .finally(() => {
        if (isMounted) {
          setDomainsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    if (!company.domainId) {
      setSubdomainOptions([]);
      return () => {
        isMounted = false;
      };
    }

    setSubdomainsLoading(true);

    getCompanyDomains(company.domainId)
      .then((subdomains) => {
        if (isMounted) {
          setSubdomainOptions(Array.isArray(subdomains) ? subdomains : []);
        }
      })
      .catch((loadError) => {
        if (isMounted) {
          setError(loadError.message || 'Impossible de charger les sous-domaines.');
        }
      })
      .finally(() => {
        if (isMounted) {
          setSubdomainsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [company.domainId]);

  const updateCompany = (event) => {
    const { name, value } = event.target;
    const nextValue = name === 'countryCode' ? value.toUpperCase().slice(0, 2) : value;
    setCompany((current) => {
      const nextCompany = { ...current, [name]: nextValue };
      if (name === 'domainId') {
        nextCompany.subdomainId = '';
      }
      return nextCompany;
    });
  };

  const toggleRole = (code) => setCompany((current) => ({
    ...current,
    roles: current.roles.includes(code) ? current.roles.filter((role) => role !== code) : [...current.roles, code],
  }));

  const updateContact = (index, event) => {
    const { name, value } = event.target;
    setContacts((current) => {
      const nextContacts = current.map((contact, contactIndex) => (
        contactIndex === index ? updatePartnerContactField(contact, name, value) : contact
      ));
      return nextContacts;
    });
  };

  const updateAgency = (index, event) => {
    const { name, value } = event.target;
    const nextValue = name === 'countryCode' ? value.toUpperCase().slice(0, 2) : value;
    setAgencies((current) => {
      const nextAgencies = current.map((agency, agencyIndex) => (
        agencyIndex === index ? { ...agency, [name]: nextValue } : agency
      ));
      return nextAgencies;
    });
  };

  const addContact = () => setContacts((current) => [...current, { ...emptyContact }]);

  const removeContact = (index) => setContacts((current) => {
    return current.filter((_, contactIndex) => contactIndex !== index);
  });

  const addAgency = () => setAgencies((current) => {
    return [...current, createAgencyDraft()];
  });

  const removeAgency = (index) => setAgencies((current) => {
    return current.filter((_, agencyIndex) => agencyIndex !== index);
  });

  const goNext = () => {
    setError(null);
    setActiveStep((current) => Math.min(current + 1, steps.length - 1));
  };

  const goBack = () => {
    setError(null);
    setActiveStep((current) => Math.max(current - 1, 0));
  };

  const clearDraft = () => {
    setDraft({
      activeStep: 0,
      company: initialCompany,
      contacts: [{ ...emptyContact }],
      agencies: [],
    });
    window.localStorage.removeItem(draftStorageKey);
  };

  const buildPayload = () => ({
    ...buildPartnerPayload({
      ...company,
      socialReason: company.socialReason || company.name,
    }),
    contacts: contacts.filter(hasValue).map(normalizePartnerContact),
    agencies: agencies.filter(hasValue).map((agency) => normalizePartnerAgency({
      ...agency,
      socialReason: agency.socialReason || agency.name,
    })),
  });

  const handleSubmit = async () => {
    const partnerValidation = validatePartner(company);
    const contactValidations = contacts.filter(hasValue).map(validatePartnerContact);
    const agencyValidations = agencies.filter(hasValue).map(validatePartnerAgency);
    const invalidContactIndex = contactValidations.findIndex(({ isValid }) => !isValid);
    const invalidAgencyIndex = agencyValidations.findIndex(({ isValid }) => !isValid);

    if (!partnerValidation.isValid) {
      setError(Object.values(partnerValidation.fieldErrors).join(' '));
      setActiveStep(0);
      return;
    }
    if (invalidContactIndex >= 0) {
      setError(`Contact ${invalidContactIndex + 1} : ${Object.values(contactValidations[invalidContactIndex].fieldErrors).join(' ')}`);
      setActiveStep(3);
      return;
    }
    if (invalidAgencyIndex >= 0) {
      setError(`Agence ${invalidAgencyIndex + 1} : ${Object.values(agencyValidations[invalidAgencyIndex].fieldErrors).join(' ')}`);
      setActiveStep(2);
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const createdPartner = await createPartner(buildPayload());
      clearDraft();
      navigate(createdPartner?.id ? `/partenaires/${createdPartner.id}` : '/partenaires');
    } catch (submitError) {
      setError(submitError.message || 'Impossible de créer le partenaire');
    } finally {
      setSubmitting(false);
    }
  };

  const renderSelect = (label, name, value, onChange, options, placeholder = 'Sélectionner', disabled = false) => (
    <div className="custom-input custom-input--full">
      <label className="custom-input__label" htmlFor={name}>
        {label}
      </label>
      <select id={name} className="custom-input__field" name={name} value={value} onChange={onChange} disabled={disabled}>
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option.key || option.id || option} value={option.key || option.id || option}>
            {option.label || option.name || option}
          </option>
        ))}
      </select>
    </div>
  );

  const renderCompanyStep = () => (
    <div className="project-creation-grid">
      <Input fullWidth required label="Raison sociale" name="socialReason" value={company.socialReason} onChange={updateCompany} />
      <Input fullWidth label="Nom commercial" name="name" value={company.name} onChange={updateCompany} />
      <fieldset className="partner-role-picker"><legend>Rôles</legend><p className="partner-form-help">Sélectionnez les fonctions de cette organisation dans votre écosystème.</p>{ASSIGNABLE_PARTNER_ROLE_OPTIONS.map((role) => <label key={role.code}><input type="checkbox" checked={company.roles.includes(role.code)} onChange={() => toggleRole(role.code)} /> {role.label}</label>)}</fieldset>
      {renderSelect('Relation', 'relationshipStatus', company.relationshipStatus, updateCompany, PARTNER_RELATIONSHIP_STATUSES)}
      {renderSelect("Source d’acquisition", 'originSource', company.originSource, updateCompany, PARTNER_SOURCES)}
      <p className="partner-form-help">Prospect : aucun devis encore accepté. Client : au moins un devis accepté. Client inactif : ancien client sans activité commerciale récente.</p>
    </div>
  );

  const renderAdditionalInformation = () => (
    <div className="project-creation-stack">
      <details className="partner-optional-section" open><summary>Informations légales et qualification</summary><div className="project-creation-grid">
      <Input fullWidth label="Nom légal" name="legalName" value={company.legalName} onChange={updateCompany} />
      <Input fullWidth label="Effectif" name="employees" value={company.employees} onChange={updateCompany} />
      <Input fullWidth label="Capital" name="capital" value={company.capital} onChange={updateCompany} />
      <Input fullWidth label="TVA intracommunautaire" name="vat" value={company.vat} onChange={updateCompany} />
      <Input fullWidth label="SIREN" name="siren" value={company.siren} onChange={updateCompany} inputProps={{ maxLength: 9 }} />
      <Input fullWidth label="SIRET" name="siret" value={company.siret} onChange={updateCompany} inputProps={{ maxLength: 14 }} />
      <Input fullWidth label="Code NAF" name="nafCode" value={company.nafCode} onChange={updateCompany} />
      {renderSelect(
        'Domaine',
        'domainId',
        company.domainId,
        updateCompany,
        domainOptions,
        domainsLoading ? 'Chargement...' : 'Sélectionner un domaine',
        domainsLoading,
      )}
      {renderSelect(
        'Sous-domaine',
        'subdomainId',
        company.subdomainId,
        updateCompany,
        subdomainOptions,
        subdomainsLoading
          ? 'Chargement...'
          : company.domainId && subdomainOptions.length === 0
            ? 'Aucun sous-domaine'
            : company.domainId
              ? 'Sélectionner un sous-domaine'
              : "Sélectionner un domaine d'abord",
        !company.domainId || subdomainsLoading || subdomainOptions.length === 0,
      )}
      </div></details>
      <details className="partner-optional-section"><summary>Agences ({agencies.filter(hasValue).length})</summary>{renderAgenciesStep()}</details>
    </div>
  );

  const renderAddressStep = () => (
    <div className="project-creation-grid">
      <Input fullWidth label="Adresse" name="addressLine1" value={company.addressLine1} onChange={updateCompany} />
      <Input fullWidth label="Complément d’adresse" name="addressLine2" value={company.addressLine2} onChange={updateCompany} />
      <Input fullWidth label="Ville" name="city" value={company.city} onChange={updateCompany} />
      <Input fullWidth label="Région / État" name="regionOrState" value={company.regionOrState} onChange={updateCompany} />
      <Input fullWidth label="Code postal" name="postalCode" value={company.postalCode} onChange={updateCompany} />
      {renderSelect('Pays', 'countryCode', company.countryCode, updateCompany, [{ key: 'FR', label: 'France' }, { key: 'BE', label: 'Belgique' }, { key: 'CH', label: 'Suisse' }, { key: 'LU', label: 'Luxembourg' }], 'Sélectionner un pays')}
      <Input fullWidth label="Téléphone" name="phone" type="tel" value={company.phone} onChange={updateCompany} />
      <Input fullWidth label="Email" name="email" type="email" value={company.email} onChange={updateCompany} />
      <Input fullWidth label="Site web" name="website" value={company.website} onChange={updateCompany} />
      <Input fullWidth multiline rows={4} label="Notes" name="notes" value={company.notes} onChange={updateCompany} />
    </div>
  );

  const renderContactFields = (contact, index) => (
    <section className="project-creation-section" key={`contact-${index}`}>
      <div className="project-creation-header">
        <Typography variant="h6">Contact {index + 1}</Typography>
        {contacts.length > 1 && (
          <button className="basic-button" type="button" onClick={() => removeContact(index)}>
            Retirer
          </button>
        )}
      </div>
      <div className="project-creation-grid">
        <Input fullWidth label="Prénom" name="firstName" value={contact.firstName} onChange={(event) => updateContact(index, event)} />
        <Input fullWidth label="Nom" name="lastName" value={contact.lastName} onChange={(event) => updateContact(index, event)} />
        <Input fullWidth label="Nom affiche" name="contactName" value={contact.contactName} onChange={(event) => updateContact(index, event)} />
        <div className="custom-input custom-input--full">
          <label className="custom-input__label" htmlFor={`contact-agency-${index}`}>
            Agence de rattachement
          </label>
          <select
            id={`contact-agency-${index}`}
            className="custom-input__field"
            name="agencyClientKey"
            value={contact.agencyClientKey || ''}
            onChange={(event) => updateContact(index, event)}
          >
            <option value="">Société principale</option>
            {agencies.filter(hasValue).map((agency, agencyIndex) => (
              <option key={agency.clientKey || agencyIndex} value={agency.clientKey || ''}>
                {agency.name || agency.socialReason || `Agence ${agencyIndex + 1}`}
              </option>
            ))}
          </select>
        </div>
        <Input fullWidth label="Role" name="role" value={contact.role} onChange={(event) => updateContact(index, event)} />
        <Input fullWidth label="Email" name="email" type="email" value={contact.email} onChange={(event) => updateContact(index, event)} />
        <Input fullWidth label="Telephone" name="phone" type="tel" value={contact.phone} onChange={(event) => updateContact(index, event)} />
        <Input fullWidth label="Mobile" name="mobilePhone" type="tel" value={contact.mobilePhone} onChange={(event) => updateContact(index, event)} />
        <Input fullWidth multiline rows={3} label="Notes" name="notes" value={contact.notes} onChange={(event) => updateContact(index, event)} />
      </div>
    </section>
  );

  const renderContactsStep = () => (
    <div className="project-creation-stack">
      {contacts.map(renderContactFields)}
      <button className="basic-button partner-add-row" type="button" onClick={addContact}>
        Ajouter un autre contact
      </button>
    </div>
  );

  const renderAgencyFields = (agency, index) => (
    <section className="project-creation-section" key={`agency-${index}`}>
      <div className="project-creation-header">
        <Typography variant="h6">Agence {index + 1}</Typography>
        <button className="basic-button" type="button" onClick={() => removeAgency(index)}>
          Retirer
        </button>
      </div>
      <div className="project-creation-grid">
        <Input fullWidth label="Nom agence" name="name" value={agency.name} onChange={(event) => updateAgency(index, event)} />
        <Input fullWidth label="Raison sociale agence" name="socialReason" value={agency.socialReason} onChange={(event) => updateAgency(index, event)} />
        <Input fullWidth label="Adresse" name="addressLine1" value={agency.addressLine1} onChange={(event) => updateAgency(index, event)} />
        <Input fullWidth label="Complément d’adresse" name="addressLine2" value={agency.addressLine2} onChange={(event) => updateAgency(index, event)} />
        <Input fullWidth label="Ville" name="city" value={agency.city} onChange={(event) => updateAgency(index, event)} />
        <Input fullWidth label="Région / État" name="regionOrState" value={agency.regionOrState} onChange={(event) => updateAgency(index, event)} />
        <Input fullWidth label="Code postal" name="postalCode" value={agency.postalCode} onChange={(event) => updateAgency(index, event)} />
        {renderSelect('Pays', 'countryCode', agency.countryCode, (event) => updateAgency(index, event), [{ key: 'FR', label: 'France' }, { key: 'BE', label: 'Belgique' }, { key: 'CH', label: 'Suisse' }, { key: 'LU', label: 'Luxembourg' }], 'Sélectionner un pays')}
        <Input fullWidth label="Téléphone" name="phone" type="tel" value={agency.phone} onChange={(event) => updateAgency(index, event)} />
        <Input fullWidth label="Email" name="email" type="email" value={agency.email} onChange={(event) => updateAgency(index, event)} />
        <Input fullWidth label="Site web" name="website" value={agency.website} onChange={(event) => updateAgency(index, event)} />
      </div>
    </section>
  );

  const renderAgenciesStep = () => (
    <div className="project-creation-stack">
      {agencies.length === 0 ? (
        <section className="project-creation-section">
          <Typography variant="body1">Aucune agence ajoutée.</Typography>
        </section>
      ) : agencies.map(renderAgencyFields)}
      <button className="basic-button" type="button" onClick={addAgency}>
        Ajouter une agence
      </button>
    </div>
  );

  const renderSummaryItem = (label, value) => (
    <div className="project-creation-summary-item">
      <span>{label}</span>
      <strong>{value || '—'}</strong>
    </div>
  );

  const renderSummaryStep = () => (
    <div className="project-creation-summary">
      <section className="project-creation-section">
        <Typography variant="h6">Société</Typography>
        {renderSummaryItem('Raison sociale', company.socialReason)}
        {renderSummaryItem('Nom commercial', company.name)}
        {renderSummaryItem('Rôles', company.roles.map((code) => PARTNER_ROLE_OPTIONS.find((role) => role.code === code)?.label || code).join(', '))}
        {renderSummaryItem('Statut administratif', getPartnerLifecycle(company.lifecycleStatus).label)}
        {renderSummaryItem('Relation', PARTNER_RELATIONSHIP_STATUSES.find((status) => status.key === company.relationshipStatus)?.label)}
        {renderSummaryItem("Source d'acquisition", company.originSource)}
        {renderSummaryItem('SIRET', company.siret)}
      </section>
      <section className="project-creation-section">
        <Typography variant="h6">Coordonnées</Typography>
        {renderSummaryItem('Adresse', company.address || company.addressLine1)}
        {renderSummaryItem('Ville', company.city)}
        {renderSummaryItem('Pays', ({ FR: 'France', BE: 'Belgique', CH: 'Suisse', LU: 'Luxembourg' })[company.countryCode] || company.countryCode)}
        {renderSummaryItem('Téléphone', company.phone)}
        {renderSummaryItem('Email', company.email)}
      </section>
      <section className="project-creation-section">
        <Typography variant="h6">Relations</Typography>
        {renderSummaryItem('Agences', agencies.filter(hasValue).length)}
        {renderSummaryItem('Contacts', contacts.filter(hasValue).length)}
      </section>
    </div>
  );

  const renderCurrentStep = () => {
    if (currentStep === 'company') return renderCompanyStep();
    if (currentStep === 'address') return renderAddressStep();
    if (currentStep === 'agencies') return renderAdditionalInformation();
    if (currentStep === 'contacts') return renderContactsStep();
    return renderSummaryStep();
  };

  return (
    <Box className="page-container module-page project-creation-page">
      <div className="project-creation-header">
        <div>
          <Typography variant="h4">Créer un partenaire</Typography>
          <Typography variant="body2" color="textSecondary">
            Société, contacts et agences rattachées.
          </Typography>
        </div>
        <button className="basic-button" type="button" onClick={() => navigate('/partenaires')}>
          Retour au carnet
        </button>
      </div>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper className="project-creation-panel">
        <div className="project-creation-stepper">
          {steps.map((step, index) => (
            <button
              key={step.key}
              className={`project-creation-step ${index === activeStep ? 'active' : ''} ${index < activeStep ? 'complete' : ''}`}
              type="button"
              onClick={() => index < activeStep && setActiveStep(index)}
            >
              <span>{index + 1}</span>
              {step.label}
            </button>
          ))}
        </div>

        <div className="project-creation-content">
          {renderCurrentStep()}
        </div>

        <div className="project-creation-actions">
          <button className="basic-button" type="button" onClick={goBack} disabled={activeStep === 0 || submitting}>
            Précédent
          </button>
          {currentStep === 'summary' ? (
            <button className="basic-button active" type="button" onClick={handleSubmit} disabled={submitting}>
              {submitting ? 'Création...' : 'Valider et créer le partenaire'}
            </button>
          ) : (
            <button className="basic-button active" type="button" onClick={goNext} disabled={submitting}>
              Suivant
            </button>
          )}
        </div>
      </Paper>
    </Box>
  );
}

export default PartnerCreation;
